import {Component, OnInit, OnDestroy, Renderer2, ChangeDetectorRef, ElementRef, ViewChild} from "@angular/core";
import {Router} from "@angular/router";
import {first, Subscription} from "rxjs";
import {BackendService} from "./services/backend/backend.service";
import {StoreService} from "./services/store.service";
import {AuthConfig, OAuthEvent, OAuthService} from "angular-oauth2-oidc";
import {AUTH0_CONFIG, IDP_PROVIDER, IS_TRUE, LOGIN_IDP, LOGIN_STATUS, TESCO_CONFIG} from "./config/constants";
import {AuthorizationService} from "./services/authorization.service";
import {CookieService} from "ngx-cookie-service";
import { SiteFilterService } from "./services/site-filter.service";
import { SelectedSite } from "./models/site";
import { SvgService } from "./services/svg/svg.service";

export function getEnvironmentFromUrl(): string {
    switch (window.location.hostname) {
        case "localhost":
        case "dev.cbms.eon-optimum.com":
        case "dashboard.dev.cbms.eon-optimum.com":
            return "DEV";
        default:
            return "PROD";
    }
}

const ENV = getEnvironmentFromUrl();

const WHITE_LIST_URLS = ['/error', '/registration/error', '/no-access'];
const REFRESH_NOTIFICATION_INTERVAL: number = 5 * 60 * 1000; //5 minutes
@Component({
    standalone: false,
    selector: "cc-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
    title = "optimum-cc-dashboard-ui";

    public user: string | undefined = "";
    public loading = false;
    public shouldShowSiteFilter = true;
    public shouldHideHeader = false;
    public userIsPresent = false;
    public isBurgerMenuActive = false;

    public isIframe = false;
    isSiteOverviewEnable: boolean;
    siteOverviewUrl: string = '';
    private siteSubcription = new Subscription();
    private notiSubcription = new Subscription();
    isPublicPage = false;
    notiList: string[] = [];
    // notiList: string[] = ['111 ADMIN_APPLICATION alo test message 333', 'ADMIN_APPLICATION alo test message 333', 'ADMIN_APPLICATION alo test message 333'];
    intervalId: any;
    isBannerClosed = false;
    //Create variables to display notification dinamically
    @ViewChild('notiContainer') containerRef!: ElementRef<HTMLDivElement>;
    @ViewChild('notiText') textRef!: ElementRef<HTMLSpanElement>;
    animationFrameId: number | null = null;
    left: number = 0;
    speed: number = 0.8; // pixels per frame
    paused: boolean = false;
    containerWidth = 0;
    textWidth = 0;

    constructor(
        private svgService: SvgService,
        private authService: OAuthService,
        private router: Router,
        private backendService: BackendService,
        private storeService: StoreService,
        private renderer: Renderer2,
        private authorizationService: AuthorizationService,
        private cookieService: CookieService,
        private siteFilterService: SiteFilterService,
        private cdr: ChangeDetectorRef
    ) {

        const loginIdp = this.cookieService.get(LOGIN_IDP) || IDP_PROVIDER.AUTH0;
        let authConfig: AuthConfig = AUTH0_CONFIG[ENV];

        if (loginIdp.startsWith('tesco-')) {
            authConfig = TESCO_CONFIG[ENV];
        }

        this.authService.events.subscribe((event: OAuthEvent) => {
            if (event.type === 'invalid_nonce_in_state') {
                window.location.href = '/equipments';
            } else if (event.type === 'token_received') {
                this.loadUserSettings();
            }
        });


        const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop) => searchParams.get(prop as string),
        });

        if (params && params['invitation'] && params['organization'] && params['organization_name']) {
            authConfig.customQueryParams = {
                invitation: params['invitation'],
                organization: params['organization'],
                organization_name: params['organization_name'],
            };
        }

        this.authService.configure(authConfig);

        this.isPublicPage = WHITE_LIST_URLS.some((path) => window.location.pathname.includes(path));
        if (!this.isPublicPage) {
            this.authService.loadDiscoveryDocumentAndLogin().then((value) => {
                if (value) {
                    this.backendService.updateLastLogin().subscribe();
                    this.authService.setupAutomaticSilentRefresh(undefined, 'id_token');
                    localStorage.setItem(LOGIN_STATUS, 'true');
                    this.authorizationService.init();
                    this.user = this.authorizationService.getUsername();
                    this.storeService.updateLoginStatus(IS_TRUE);
                    this.getImportantNotification();
                    this.intervalId = setInterval(() => {
                        if (this.authService.hasValidAccessToken()) {
                            this.getImportantNotification();
                        } else {
                            clearInterval(this.intervalId);
                        }
                    }, REFRESH_NOTIFICATION_INTERVAL);
                }
            });
        }

    }

    ngOnInit(): void {
        this.siteSubcription = this.siteFilterService.siteFilterChanged$.subscribe((selectedSite) => {
            if (selectedSite) {
                this.handleSiteOverviewTab(selectedSite);
            }
        });
        this.isIframe = window !== window.parent && !window.opener;
        this.loadUserSettings();
    }

    handleSiteOverviewTab(selectedSite: SelectedSite) {
        this.siteOverviewUrl = `site-overview/site/${selectedSite.siteId}`;
        this.isSiteOverviewEnable = JSON.parse(sessionStorage.getItem('selectedSite'))?.siteOverviewEnabled;
    }


    logout() {
        this.authorizationService.logout();
    }

    openBurgerMenuOnMobile() {
        this.isBurgerMenuActive = true;
        this.renderer.addClass(document.body, "noscroll");
        this.renderer.addClass(
            document.getElementById("app-wrapper"),
            "noscroll"
        );
    }

    closeBurgerMenuOnMobile() {
        this.isBurgerMenuActive = false;
        this.renderer.removeClass(document.body, "noscroll");
        this.renderer.removeClass(
            document.getElementById("app-wrapper"),
            "noscroll"
        );
    }

    ngOnDestroy(): void {
        this.siteSubcription?.unsubscribe();
        this.notiSubcription?.unsubscribe();
        clearInterval(this.intervalId);
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
        }
    }

    private loadUserSettings(): void {
        if (this.authService.hasValidAccessToken()) {
            this.backendService
                .checkUserNamePresence(
                    this.authorizationService.getUsername()
                )
                .subscribe((response: any) => {
                    this.userIsPresent = response.isPresent;
                    if (response.isPresent) {
                        this.user = this.authorizationService.getUsername();

                        this.backendService
                            .checkTermsOfUseStatus(this.user)
                            .subscribe((response) => {
                                if (!response.hasAgreedTermsAndConditions) {
                                    this.router.navigate([
                                        `/terms-and-conditions`,
                                    ]);
                                }
                            });
                    }
                });

            this.backendService
                .checkUserWritePermission()
                .subscribe((response: any) => {
                    this.storeService.updateUserWritePermission(
                        response.hasWritePermission
                    );

                    this.storeService.value(response.hasWritePermission);
                });
        }
    }

    getImportantNotification() {
        this.notiSubcription = this.backendService.getImportantNotification().pipe(first()).subscribe({
            next: (res: string[]) => {
                this.notiList = res;
                this.cdr.detectChanges();
                this.isBannerClosed = !res?.length
                setTimeout(() => {
                    if (this.notiList?.length) {
                        this.containerWidth = this.containerRef.nativeElement.offsetWidth - 30; //30px is width of the close button
                        this.textWidth = this.textRef.nativeElement.offsetWidth;
                        this.left = this.containerWidth;
                        this.animate();
                    }
                }, 0);
                if (this.animationFrameId) {
                  cancelAnimationFrame(this.animationFrameId);
                }
            }
        })
    }

    animate = () => {
        if (!this.paused) {
            this.left -= this.speed;
            this.textRef.nativeElement.style.transform = `translateX(${this.left}px)`;

            if (this.left <= -this.textWidth) {
                this.left = this.containerWidth;
            }
        }
        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    pauseMarquee() {
        this.paused = true;
    }

    resumeMarquee() {
        this.paused = false;
    }
}
